import { StorageService } from '../core/Storage.ts';
import { GAME_CONFIG } from '../config.ts';

export type SoundType =
  | 'girl'
  | 'penis'
  | 'grenade'
  | 'monkey'
  | 'dog'
  | 'levelup'
  | 'gameover'
  | 'click'
  | 'combo';

export type BgmMode = 'none' | 'menu' | 'game';

export class AudioSystem {
  private ctx: AudioContext | null = null;
  private isMuted: boolean = false;
  private isUnlocked: boolean = false;
  private customBuffers: Map<string, AudioBuffer> = new Map();
  private bgmGainNode: GainNode | null = null;
  private sfxGainNode: GainNode | null = null;
  private bgmMode: BgmMode = 'none';
  private isSuspended: boolean = false;
  private musicStep: number = 0;
  private menuStep: number = 0;
  private musicTimer: number | null = null;
  private currentBpm: number = 105;

  // Voice reaction samples
  private girlBuffers: AudioBuffer[] = [];
  private girlBufferIndex: number = 0;
  private penisBuffer: AudioBuffer | null = null;
  private preloadedBuffers: Map<string, ArrayBuffer> = new Map();
  private isPreloading: boolean = false;

  // Preload audio asset URLs
  private static readonly PRELOAD_AUDIO_URLS: string[] = [
    './assets/audio/girl-2.mp3',
    './assets/audio/penis.mp3'
  ];

  // Master volume balance constants
  private static readonly SFX_MASTER_VOLUME: number = 0.45;
  private static readonly BGM_MASTER_VOLUME: number = 0.45;

  // Active voice instance reference (for voice ducking / duration capping)
  private activeVoiceGain: GainNode | null = null;

  constructor() {
    this.isMuted = StorageService.isSoundMuted();
    this.preloadAudio();
  }

  public preloadAudio(): void {
    if (this.isPreloading) return;
    this.isPreloading = true;

    AudioSystem.PRELOAD_AUDIO_URLS.forEach((url, index) => {
      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP error ${res.status}`);
          return res.arrayBuffer();
        })
        .then((arrayBuffer) => {
          this.preloadedBuffers.set(url, arrayBuffer);
          if (this.ctx && this.isUnlocked) {
            this.decodeBuffer(url, arrayBuffer, index);
          }
        })
        .catch((err) => {
          console.warn(`Audio preload fallback for ${url}:`, err);
        });
    });
  }

  public init(): void {
    if (this.ctx) return;
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;

    this.ctx = new AudioContextClass();

    this.sfxGainNode = this.ctx.createGain();
    this.sfxGainNode.gain.value = this.isMuted ? 0 : AudioSystem.SFX_MASTER_VOLUME;
    this.sfxGainNode.connect(this.ctx.destination);

    this.bgmGainNode = this.ctx.createGain();
    this.bgmGainNode.gain.value = this.isMuted ? 0 : AudioSystem.BGM_MASTER_VOLUME;
    this.bgmGainNode.connect(this.ctx.destination);
  }

  public unlock(): void {
    this.init();
    if (!this.ctx) return;

    const onUnlocked = () => {
      this.isUnlocked = true;
      this.decodeAllPending();
      if (this.bgmMode === 'menu' && this.musicTimer === null) {
        this.scheduleNextMenuBeat();
      }
    };

    // Bulletproof iOS Safari audio activation with silent buffer playback
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().then(() => {
        this.playSilentBuffer();
        onUnlocked();
      }).catch(() => {});
    } else {
      this.playSilentBuffer();
      onUnlocked();
    }
  }

  private decodeAllPending(): void {
    if (!this.ctx) return;

    AudioSystem.PRELOAD_AUDIO_URLS.forEach((url, index) => {
      const raw = this.preloadedBuffers.get(url);
      if (raw) {
        if (url.includes('penis')) {
          if (!this.penisBuffer) {
            this.decodeBuffer(url, raw, index);
          }
        } else {
          if (!this.girlBuffers[index]) {
            this.decodeBuffer(url, raw, index);
          }
        }
      }
    });
  }

  private async decodeBuffer(url: string, raw: ArrayBuffer, index: number): Promise<void> {
    if (!this.ctx) return;
    try {
      const bufferToDecode = raw.slice(0);
      const decoded = await this.ctx.decodeAudioData(bufferToDecode);
      if (url.includes('penis')) {
        this.penisBuffer = decoded;
      } else {
        this.girlBuffers[index] = decoded;
      }
    } catch (err) {
      console.warn(`Failed to decode ${url}:`, err);
    }
  }

  private playSilentBuffer(): void {
    if (!this.ctx) return;
    try {
      const buffer = this.ctx.createBuffer(1, 1, 22050);
      const source = this.ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(this.ctx.destination);
      source.start(0);
    } catch {
      // Ignore
    }
  }

  public pauseAudio(): void {
    this.isSuspended = true;
    if (this.ctx && this.ctx.state === 'running') {
      this.ctx.suspend().catch(() => {});
    }
  }

  public resumeAudio(): void {
    this.isSuspended = false;
    if (this.ctx && this.ctx.state === 'suspended' && this.isUnlocked) {
      this.ctx.resume().catch(() => {});
    }
  }

  public toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    StorageService.setSoundMuted(this.isMuted);

    if (this.sfxGainNode) {
      this.sfxGainNode.gain.value = this.isMuted ? 0 : AudioSystem.SFX_MASTER_VOLUME;
    }
    if (this.bgmGainNode) {
      this.bgmGainNode.gain.value = this.isMuted ? 0 : AudioSystem.BGM_MASTER_VOLUME;
    }

    return this.isMuted;
  }

  public getMuted(): boolean {
    return this.isMuted;
  }

  public setLevelBpm(bpm: number): void {
    this.currentBpm = bpm;
  }

  public play(type: SoundType): void {
    if (this.isMuted || !this.ctx || !this.isUnlocked || this.isSuspended) return;

    // Check if we have an external recorded audio buffer
    if (this.customBuffers.has(type)) {
      this.playBuffer(type);
      return;
    }

    const now = this.ctx.currentTime;
    switch (type) {
      case 'girl':
        this.playGirlSound(now);
        break;
      case 'penis':
        this.playPenisSound(now);
        break;
      case 'grenade':
        this.synthExplosion(now);
        break;
      case 'monkey':
        this.synthMonkey(now);
        break;
      case 'dog':
        this.synthDogBark(now);
        break;
      case 'levelup':
        this.synthLevelUpFanfare(now);
        break;
      case 'gameover':
        this.synthGameOver(now);
        break;
      case 'click':
        this.synthClick(now);
        break;
      case 'combo':
        this.synthComboChime(now);
        break;
    }
  }

  // --- Procedural Sound Synthesizers ---

  private synthSensualMoan(t: number): void {
    if (!this.ctx || !this.sfxGainNode) return;
    const osc = this.ctx.createOscillator();
    const filter = this.ctx.createBiquadFilter();
    const gain = this.ctx.createGain();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(420, t);
    osc.frequency.exponentialRampToValueAtTime(530, t + 0.25);
    osc.frequency.exponentialRampToValueAtTime(360, t + 0.85);

    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(950, t);
    filter.frequency.exponentialRampToValueAtTime(1400, t + 0.3);
    filter.frequency.exponentialRampToValueAtTime(800, t + 0.85);
    filter.Q.value = 4.0;

    gain.gain.setValueAtTime(0.001, t);
    gain.gain.linearRampToValueAtTime(0.42, t + 0.2);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.85);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.sfxGainNode);

    osc.start(t);
    osc.stop(t + 0.9);
  }

  private synthFunnySlip(t: number): void {
    if (!this.ctx || !this.sfxGainNode) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(680, t);
    osc.frequency.exponentialRampToValueAtTime(140, t + 0.45);

    gain.gain.setValueAtTime(0.35, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);

    osc.connect(gain);
    gain.connect(this.sfxGainNode);

    osc.start(t);
    osc.stop(t + 0.5);
  }

  private synthExplosion(t: number): void {
    if (!this.ctx || !this.sfxGainNode) return;
    const bufferSize = this.ctx.sampleRate * 0.8;
    const noiseBuffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }

    const whiteNoise = this.ctx.createBufferSource();
    whiteNoise.buffer = noiseBuffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(800, t);
    filter.frequency.exponentialRampToValueAtTime(60, t + 0.7);

    const noiseGain = this.ctx.createGain();
    noiseGain.gain.setValueAtTime(0.8, t);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.75);

    whiteNoise.connect(filter);
    filter.connect(noiseGain);
    noiseGain.connect(this.sfxGainNode);

    const subOsc = this.ctx.createOscillator();
    const subGain = this.ctx.createGain();
    subOsc.type = 'sine';
    subOsc.frequency.setValueAtTime(120, t);
    subOsc.frequency.exponentialRampToValueAtTime(30, t + 0.6);
    subGain.gain.setValueAtTime(0.7, t);
    subGain.gain.exponentialRampToValueAtTime(0.001, t + 0.6);

    subOsc.connect(subGain);
    subGain.connect(this.sfxGainNode);

    whiteNoise.start(t);
    whiteNoise.stop(t + 0.8);
    subOsc.start(t);
    subOsc.stop(t + 0.65);
  }

  private synthMonkey(t: number): void {
    if (!this.ctx || !this.sfxGainNode) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'square';
    osc.frequency.setValueAtTime(800, t);
    osc.frequency.linearRampToValueAtTime(1300, t + 0.08);
    osc.frequency.linearRampToValueAtTime(750, t + 0.16);
    osc.frequency.linearRampToValueAtTime(1450, t + 0.28);
    osc.frequency.linearRampToValueAtTime(600, t + 0.45);

    gain.gain.setValueAtTime(0.25, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.48);

    osc.connect(gain);
    gain.connect(this.sfxGainNode);

    osc.start(t);
    osc.stop(t + 0.5);
  }

  private synthDogBark(t: number): void {
    if (!this.ctx || !this.sfxGainNode) return;
    const makeBark = (time: number) => {
      if (!this.ctx || !this.sfxGainNode) return;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(450, time);
      osc.frequency.exponentialRampToValueAtTime(180, time + 0.14);

      gain.gain.setValueAtTime(0.3, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 0.15);

      osc.connect(gain);
      gain.connect(this.sfxGainNode);

      osc.start(time);
      osc.stop(time + 0.16);
    };

    makeBark(t);
    makeBark(t + 0.18);
  }

  private synthComboChime(t: number): void {
    if (!this.ctx || !this.sfxGainNode) return;
    const notes = [587.33, 880.0, 1174.66];
    notes.forEach((freq, idx) => {
      if (!this.ctx || !this.sfxGainNode) return;
      const noteTime = t + idx * 0.06;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;

      gain.gain.setValueAtTime(0.28, noteTime);
      gain.gain.exponentialRampToValueAtTime(0.001, noteTime + 0.25);

      osc.connect(gain);
      gain.connect(this.sfxGainNode);

      osc.start(noteTime);
      osc.stop(noteTime + 0.28);
    });
  }

  private synthLevelUpFanfare(t: number): void {
    if (!this.ctx || !this.sfxGainNode) return;
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((freq, idx) => {
      if (!this.ctx || !this.sfxGainNode) return;
      const noteTime = t + idx * 0.09;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.value = freq;

      gain.gain.setValueAtTime(0.25, noteTime);
      gain.gain.exponentialRampToValueAtTime(0.001, noteTime + 0.28);

      osc.connect(gain);
      gain.connect(this.sfxGainNode);

      osc.start(noteTime);
      osc.stop(noteTime + 0.3);
    });
  }

  private synthGameOver(t: number): void {
    if (!this.ctx || !this.sfxGainNode) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(330, t);
    osc.frequency.linearRampToValueAtTime(310, t + 0.3);
    osc.frequency.linearRampToValueAtTime(290, t + 0.6);
    osc.frequency.linearRampToValueAtTime(220, t + 1.2);

    gain.gain.setValueAtTime(0.35, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 1.25);

    osc.connect(gain);
    gain.connect(this.sfxGainNode);

    osc.start(t);
    osc.stop(t + 1.3);
  }

  private synthClick(t: number): void {
    if (!this.ctx || !this.sfxGainNode) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(900, t);
    osc.frequency.exponentialRampToValueAtTime(300, t + 0.05);
    gain.gain.setValueAtTime(0.2, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);

    osc.connect(gain);
    gain.connect(this.sfxGainNode);

    osc.start(t);
    osc.stop(t + 0.06);
  }

  // --- Dynamic Procedural BGM (Background Music) ---

  public startMusic(): void {
    const wasMenu = this.bgmMode === 'menu';
    this.stopMusic();
    this.bgmMode = 'game';
    this.musicStep = 0;

    if (wasMenu && this.ctx && this.isUnlocked) {
      this.playTransitionSweep(this.ctx.currentTime);
    }
    this.scheduleNextBeat();
  }

  public startMenuMusic(): void {
    if (this.bgmMode === 'menu') return;
    this.stopMusic();
    this.bgmMode = 'menu';
    this.menuStep = 0;

    if (this.ctx && this.isUnlocked) {
      this.scheduleNextMenuBeat();
    }
  }

  public stopMusic(): void {
    this.bgmMode = 'none';
    if (this.musicTimer !== null) {
      window.clearTimeout(this.musicTimer);
      this.musicTimer = null;
    }
  }

  // --- Gameplay Synthwave Sequencer (105 - 142 BPM) ---

  private scheduleNextBeat(): void {
    if (this.bgmMode !== 'game') {
      return;
    }

    if (!this.ctx || !this.isUnlocked || !this.bgmGainNode || this.isSuspended) {
      this.musicTimer = window.setTimeout(() => this.scheduleNextBeat(), 200);
      return;
    }

    const intervalMs = (60 / this.currentBpm / 2) * 1000;
    const t = this.ctx.currentTime + 0.05;

    const stepInBar = this.musicStep % 8;
    if (stepInBar === 0 || stepInBar === 4) {
      this.playProceduralKick(t);
    } else if (stepInBar === 2 || stepInBar === 6) {
      this.playProceduralClap(t);
    }

    const bassScale = [55, 55, 65.41, 73.42, 55, 65.41, 82.41, 73.42];
    this.playProceduralBass(t, bassScale[stepInBar], (intervalMs / 1000) * 0.85);

    this.musicStep++;
    this.musicTimer = window.setTimeout(() => this.scheduleNextBeat(), intervalMs);
  }

  private playProceduralKick(t: number): void {
    if (!this.ctx || !this.bgmGainNode || this.isMuted) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.frequency.setValueAtTime(140, t);
    osc.frequency.exponentialRampToValueAtTime(40, t + 0.12);
    gain.gain.setValueAtTime(0.5, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    osc.connect(gain);
    gain.connect(this.bgmGainNode);
    osc.start(t);
    osc.stop(t + 0.16);
  }

  private playProceduralClap(t: number): void {
    if (!this.ctx || !this.bgmGainNode || this.isMuted) return;
    const bufferSize = this.ctx.sampleRate * 0.12;
    const noiseBuffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }
    const noise = this.ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1100;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.22, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.12);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.bgmGainNode);
    noise.start(t);
    noise.stop(t + 0.13);
  }

  private playProceduralBass(t: number, freq: number, duration: number): void {
    if (!this.ctx || !this.bgmGainNode || this.isMuted) return;
    const osc = this.ctx.createOscillator();
    const filter = this.ctx.createBiquadFilter();
    const gain = this.ctx.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(freq, t);

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(450, t);
    filter.frequency.exponentialRampToValueAtTime(120, t + duration);

    gain.gain.setValueAtTime(0.25, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.bgmGainNode);

    osc.start(t);
    osc.stop(t + duration);
  }

  // --- Neon VIP Lounge Sequencer (Menu Theme, 88 BPM) ---

  private scheduleNextMenuBeat(): void {
    if (this.bgmMode !== 'menu') {
      return;
    }

    if (!this.ctx || !this.isUnlocked || !this.bgmGainNode || this.isSuspended) {
      this.musicTimer = window.setTimeout(() => this.scheduleNextMenuBeat(), 200);
      return;
    }

    const intervalMs = (60 / 88 / 2) * 1000; // ~340.9ms per 1/8 note
    const t = this.ctx.currentTime + 0.05;

    const step = this.menuStep % 32;
    const stepInBar = step % 8;

    // Velvet Muffled Analog Pad (Chord Progression: Am9 -> Fmaj7 -> Dm9 -> E7sus4/E7)
    if (step === 0) {
      this.playLoungePad(t, [220.00, 261.63, 329.63, 392.00, 493.88], 2.65); // Am9
    } else if (step === 8) {
      this.playLoungePad(t, [174.61, 220.00, 261.63, 329.63], 2.65); // Fmaj7
    } else if (step === 16) {
      this.playLoungePad(t, [146.83, 220.00, 261.63, 329.63], 2.65); // Dm9
    } else if (step === 24) {
      this.playLoungePad(t, [164.81, 220.00, 246.94, 293.66], 1.30); // E7sus4
    } else if (step === 28) {
      this.playLoungePad(t, [164.81, 207.65, 246.94, 293.66], 1.30); // E7
    }

    // Deep Sub-Bass (Syncopated pulse on steps 0, 3, 6)
    const roots = [55.00, 43.65, 36.71, 41.20]; // A1, F1, D1, E1
    const currentRoot = roots[Math.floor(step / 8)] || 55.00;

    if (stepInBar === 0) {
      this.playLoungeSubBass(t, currentRoot, (intervalMs / 1000) * 2.6);
    } else if (stepInBar === 3) {
      this.playLoungeSubBass(t, currentRoot, (intervalMs / 1000) * 2.5);
    } else if (stepInBar === 6) {
      this.playLoungeSubBass(t, currentRoot, (intervalMs / 1000) * 1.8);
    }

    // Muffled Lounge Kick (steps 0, 4)
    if (stepInBar === 0 || stepInBar === 4) {
      this.playLoungeKick(t);
    }

    // Crisp Finger Snap / Rimshot (steps 2, 6)
    if (stepInBar === 2 || stepInBar === 6) {
      this.playLoungeSnap(t);
    }

    // Crystalline Neon Arpeggio with Delay Echo
    const arpNotes: Record<number, number> = {
      2: 659.25,  // E5
      5: 783.99,  // G5
      7: 880.00,  // A5
      10: 1046.50, // C6
      13: 987.77,  // B5
      15: 783.99,  // G5
      18: 698.46,  // F5
      21: 880.00,  // A5
      23: 659.25,  // E5
      26: 587.33,  // D5
      29: 830.61,  // G#5
      31: 987.77   // B5
    };

    if (arpNotes[step] !== undefined) {
      this.playLoungeArp(t, arpNotes[step]);
    }

    this.menuStep++;
    this.musicTimer = window.setTimeout(() => this.scheduleNextMenuBeat(), intervalMs);
  }

  private playLoungePad(t: number, freqs: number[], duration: number): void {
    if (!this.ctx || !this.bgmGainNode || this.isMuted) return;

    // Lowpass filter simulating sounds behind the VIP club velvet doors, now richer and more present
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(650, t);
    filter.frequency.linearRampToValueAtTime(950, t + duration * 0.45);
    filter.frequency.exponentialRampToValueAtTime(580, t + duration);
    filter.Q.value = 1.8;

    const masterChordGain = this.ctx.createGain();
    const attackTime = Math.min(0.35, duration * 0.2);
    const releaseTime = Math.min(0.4, duration * 0.25);
    masterChordGain.gain.setValueAtTime(0.001, t);
    masterChordGain.gain.linearRampToValueAtTime(0.42, t + attackTime);
    masterChordGain.gain.setValueAtTime(0.42, t + duration - releaseTime);
    masterChordGain.gain.exponentialRampToValueAtTime(0.0001, t + duration);

    filter.connect(masterChordGain);
    masterChordGain.connect(this.bgmGainNode);

    freqs.forEach((freq) => {
      if (!this.ctx) return;
      // Sawtooth oscillator
      const oscSaw = this.ctx.createOscillator();
      oscSaw.type = 'sawtooth';
      oscSaw.frequency.setValueAtTime(freq, t);
      oscSaw.detune.setValueAtTime(-5, t);

      // Triangle oscillator slightly detuned for chorus thickness
      const oscTri = this.ctx.createOscillator();
      oscTri.type = 'triangle';
      oscTri.frequency.setValueAtTime(freq, t);
      oscTri.detune.setValueAtTime(5, t);

      const voiceGain = this.ctx.createGain();
      voiceGain.gain.value = 1 / freqs.length;

      oscSaw.connect(voiceGain);
      oscTri.connect(voiceGain);
      voiceGain.connect(filter);

      oscSaw.start(t);
      oscSaw.stop(t + duration);
      oscTri.start(t);
      oscTri.stop(t + duration);
    });
  }

  private playLoungeSubBass(t: number, freq: number, duration: number): void {
    if (!this.ctx || !this.bgmGainNode || this.isMuted) return;

    const osc = this.ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, t);

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(220, t);
    filter.frequency.exponentialRampToValueAtTime(90, t + duration);

    const gain = this.ctx.createGain();
    const attack = 0.04;
    gain.gain.setValueAtTime(0.001, t);
    gain.gain.linearRampToValueAtTime(0.48, t + attack);
    gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.bgmGainNode);

    osc.start(t);
    osc.stop(t + duration + 0.02);
  }

  private playLoungeArp(t: number, freq: number): void {
    if (!this.ctx || !this.bgmGainNode || this.isMuted) return;

    const playPluck = (time: number, volume: number, filterFreq: number) => {
      if (!this.ctx || !this.bgmGainNode) return;
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, time);

      const filter = this.ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(filterFreq, time);
      filter.frequency.exponentialRampToValueAtTime(450, time + 0.28);

      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0.001, time);
      gain.gain.linearRampToValueAtTime(volume, time + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.3);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.bgmGainNode);

      osc.start(time);
      osc.stop(time + 0.32);
    };

    // Primary crystalline bell pluck
    playPluck(t, 0.28, 2400);
    // Simulated stereo/echo delay tap (180ms later, softer, more muffled)
    playPluck(t + 0.18, 0.12, 1400);
  }

  private playLoungeKick(t: number): void {
    if (!this.ctx || !this.bgmGainNode || this.isMuted) return;

    const osc = this.ctx.createOscillator();
    const filter = this.ctx.createBiquadFilter();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(95, t);
    osc.frequency.exponentialRampToValueAtTime(35, t + 0.18);

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(170, t);

    gain.gain.setValueAtTime(0.52, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.bgmGainNode);

    osc.start(t);
    osc.stop(t + 0.22);
  }

  private playLoungeSnap(t: number): void {
    if (!this.ctx || !this.bgmGainNode || this.isMuted) return;

    // Filtered noise burst for the snap texture
    const bufferSize = Math.floor(this.ctx.sampleRate * 0.06);
    const noiseBuffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }

    const noise = this.ctx.createBufferSource();
    noise.buffer = noiseBuffer;

    const noiseFilter = this.ctx.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.frequency.setValueAtTime(1900, t);
    noiseFilter.Q.value = 3.2;

    const noiseGain = this.ctx.createGain();
    noiseGain.gain.setValueAtTime(0.28, t);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.06);

    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(this.bgmGainNode);

    // Short wooden body pop
    const bodyOsc = this.ctx.createOscillator();
    bodyOsc.type = 'triangle';
    bodyOsc.frequency.setValueAtTime(750, t);
    bodyOsc.frequency.exponentialRampToValueAtTime(280, t + 0.05);

    const bodyGain = this.ctx.createGain();
    bodyGain.gain.setValueAtTime(0.22, t);
    bodyGain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);

    bodyOsc.connect(bodyGain);
    bodyGain.connect(this.bgmGainNode);

    noise.start(t);
    noise.stop(t + 0.07);
    bodyOsc.start(t);
    bodyOsc.stop(t + 0.06);
  }

  // --- VIP Door Opening Transition (Filter Sweep Riser) ---

  private playTransitionSweep(t: number): void {
    if (!this.ctx || !this.bgmGainNode || this.isMuted) return;

    const duration = 0.45;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(320, t);
    filter.frequency.exponentialRampToValueAtTime(3800, t + duration);
    filter.Q.value = 3.5;

    // Filtered noise riser
    const bufferSize = Math.floor(this.ctx.sampleRate * duration);
    const noiseBuffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    const noise = this.ctx.createBufferSource();
    noise.buffer = noiseBuffer;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.01, t);
    gain.gain.linearRampToValueAtTime(0.35, t + duration * 0.85);
    gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.bgmGainNode);

    // Warm synthesizer chord riser
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(110, t);
    osc.frequency.exponentialRampToValueAtTime(440, t + duration);

    const oscGain = this.ctx.createGain();
    oscGain.gain.setValueAtTime(0.01, t);
    oscGain.gain.linearRampToValueAtTime(0.2, t + duration * 0.85);
    oscGain.gain.exponentialRampToValueAtTime(0.001, t + duration);

    osc.connect(filter);
    osc.start(t);
    osc.stop(t + duration);

    noise.start(t);
    noise.stop(t + duration);
  }

  // --- Girl Voice Sample Playback ---

  public playGirlSound(t?: number): void {
    if (this.isMuted || !this.ctx || !this.isUnlocked || this.isSuspended) return;

    const validBuffers = this.girlBuffers.filter(Boolean);
    if (validBuffers.length > 0) {
      const buffer = validBuffers[this.girlBufferIndex % validBuffers.length];
      this.girlBufferIndex = (this.girlBufferIndex + 1) % validBuffers.length;

      const baseVol = 0.45;
      this.playCustomBuffer(buffer, baseVol, GAME_CONFIG.maxVoiceDuration, true);
      return;
    }

    // Procedural synthesized sensual moan fallback if audio files are unavailable or still decoding
    const now = t ?? this.ctx.currentTime;
    this.synthSensualMoan(now);
  }

  // --- Penis Catch Reaction Playback ---

  public playPenisSound(t?: number): void {
    if (this.isMuted || !this.ctx || !this.isUnlocked || this.isSuspended) return;

    if (this.penisBuffer) {
      this.playCustomBuffer(this.penisBuffer, 0.52, GAME_CONFIG.maxVoiceDuration, true);
      return;
    }

    // Procedural synthesized funny slip fallback if audio file is unavailable or still decoding
    const now = t ?? this.ctx.currentTime;
    this.synthFunnySlip(now);
  }

  private playCustomBuffer(
    buffer: AudioBuffer,
    volume: number = 1.0,
    maxDuration: number = GAME_CONFIG.maxVoiceDuration,
    isVoiceChannel: boolean = true
  ): void {
    if (!this.ctx || !this.sfxGainNode || this.isMuted) return;

    try {
      const now = this.ctx.currentTime;

      // Voice ducking / stealing: if another voice sound is currently playing,
      // smoothly ramp its gain down over 40ms to avoid an overlapping wall of cacophony
      if (isVoiceChannel && this.activeVoiceGain) {
        try {
          const prevGain = this.activeVoiceGain;
          if (typeof prevGain.gain.cancelAndHoldAtTime === 'function') {
            prevGain.gain.cancelAndHoldAtTime(now);
          } else {
            prevGain.gain.cancelScheduledValues(now);
            prevGain.gain.setValueAtTime(prevGain.gain.value, now);
          }
          prevGain.gain.linearRampToValueAtTime(0.0001, now + 0.04);
        } catch {
          // Ignore
        }
      }

      const source = this.ctx.createBufferSource();
      source.buffer = buffer;
      // Subtle natural pitch variation (0.97 - 1.03)
      const pitch = 0.97 + Math.random() * 0.06;
      source.playbackRate.value = pitch;

      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(volume, now);

      source.connect(gain);
      gain.connect(this.sfxGainNode);

      // Duration limiting: enforce maxDuration with a smooth, click-free fade-out
      const naturalDuration = buffer.duration / pitch;
      const effectiveDuration = Math.min(naturalDuration, maxDuration);

      const fadeDuration = Math.min(0.2, effectiveDuration * 0.25);
      const fadeStart = now + effectiveDuration - fadeDuration;

      // Ramp down smoothly to silence before stopping
      gain.gain.setValueAtTime(volume, fadeStart);
      gain.gain.linearRampToValueAtTime(0.0001, now + effectiveDuration);

      source.start(now);
      source.stop(now + effectiveDuration);

      if (isVoiceChannel) {
        this.activeVoiceGain = gain;
        source.onended = () => {
          if (this.activeVoiceGain === gain) {
            this.activeVoiceGain = null;
          }
        };
      }
    } catch {
      // Ignore
    }
  }

  // --- External Sample Loading ---

  public async loadExternalSound(name: string, url: string): Promise<void> {
    if (!this.ctx) return;
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const decoded = await this.ctx.decodeAudioData(arrayBuffer);
      this.customBuffers.set(name, decoded);
    } catch {
      // Keep procedural fallback if fetch fails
    }
  }

  private playBuffer(name: string): void {
    if (!this.ctx || !this.sfxGainNode || this.isMuted) return;
    const buffer = this.customBuffers.get(name);
    if (!buffer) return;

    this.playCustomBuffer(buffer, 1.0, GAME_CONFIG.maxVoiceDuration, false);
  }
}
